// @ts-nocheck
/**
 * Live Calls — realtime call progress, one route for every role.
 *
 *   role_sadmin / role_sub_superadmin  platform: pick a call centre, then its board
 *   role_reseller / role_cadmin        company: pick among own call centres (auto if one)
 *   role_ccadmin                       own call centre board
 *   team lead (access or role)         own call groups, grouped, with supervise actions
 *   role_ccagent                       own calls only
 *
 * The backend derives the scope from the JWT; the ids this page sends only
 * narrow a scope the viewer already owns. ACL module: Realtime / read.
 * Hangup button: role_sadmin only.
 */
import React, { useMemo, useState } from 'react';
import { Radio } from 'lucide-react';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { EmptyState } from '@/components/feedback/EmptyState';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { isTeamLeadUser } from '@/features/cti2/acl';
import type { LiveCallCallCentre } from './types';
import CallCentrePicker from './components/CallCentrePicker';
import LiveCallsBoard from './components/LiveCallsBoard';
import TeamLeadLiveBoard from './components/TeamLeadLiveBoard';

const PLATFORM_ROLES = [USER_ROLES.SUPERADMIN, USER_ROLES.SUB_SUPERADMIN];
const COMPANY_ROLES = [USER_ROLES.RESELLER, USER_ROLES.COMPANY_ADMIN];

export default function LiveCallsPage() {
  const user = useMemo(() => getUser(), []);
  const role = user?.role || '';
  const acl = useModuleAcl(MODULE_ACL.REALTIME);
  const isSuperAdmin = role === USER_ROLES.SUPERADMIN;
  const [cc, setCc] = useState<LiveCallCallCentre | null>(null);

  if (!isSuperAdmin && !acl.canRead) {
    return <AclDenied operation="read" />;
  }

  const isPlatform = PLATFORM_ROLES.includes(role);
  const isCompany = COMPANY_ROLES.includes(role);
  const isTeamLead = isTeamLeadUser(user);
  const isAgent = role === USER_ROLES.CALL_CENTRE_AGENT && !isTeamLead;
  const isCcAdmin = role === USER_ROLES.CALL_CENTRE_ADMIN;

  let description = 'Calls in progress right now, updated as they change';
  if (isPlatform) description = 'Every call centre on the platform — choose one from the dropdown to open its board';
  if (isCompany) description = 'Call centres under your company — choose one from the dropdown to open its board';
  if (isTeamLead) description = 'Calls of the agents in your call groups — listen, whisper or barge';
  if (isAgent) description = 'Your own calls in progress';

  return (
    <FillPage>
      <PageHeader title="Live Calls" description={description} compact />

      {isPlatform || isCompany ? (
        <>
          <CallCentrePicker selected={cc} onSelect={setCc} />
          {cc ? (
            <LiveCallsBoard
              key={`${cc.companyId}-${cc.ccId}`}
              tableId={`live-calls-cc-${cc.ccId}`}
              query={isPlatform ? { companyId: cc.companyId, ccId: cc.ccId } : { ccId: cc.ccId }}
              canHangup={isSuperAdmin}
            />
          ) : (
            <EmptyState icon={Radio} title="Choose a call centre" description="Pick a call centre from the dropdown above to see its live calls. Each option shows its in / out / int totals." />
          )}
        </>
      ) : null}

      {isCcAdmin ? <LiveCallsBoard tableId="live-calls-cc" query={{}} /> : null}

      {isTeamLead ? <TeamLeadLiveBoard /> : null}

      {isAgent ? <LiveCallsBoard tableId="live-calls-agent" query={{}} hideDirectionTabs compactKpis /> : null}

      {!isPlatform && !isCompany && !isCcAdmin && !isTeamLead && !isAgent ? (
        <EmptyState title="Live calls are not available for this role" />
      ) : null}
    </FillPage>
  );
}
